import React from 'react';
//import PropTypes from 'prop-types';
import clsx from 'clsx';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';

import { withLang } from '_core/hocs/withLang';

import { useStyle } from './style';

const buildListeners = () => {
  let dispatchTimeout = null;

  const list = [];

  const remove = (listener) => {
    const index = list.indexOf(listener);
    if (index >= 0) list.splice(index, 1);
  };
  const add = (listener) => {
    list.push(listener);
    return () => remove(listener);
  };
  const useListener = (listener, dependencies) =>
    React.useEffect(() => add(listener), dependencies);
  const dispatch = () => {
    if (dispatchTimeout) return;
    dispatchTimeout = setTimeout(() => {
      list.forEach((listener) => listener());
      dispatchTimeout = null;
    }, 50);
  };

  return { list, add, remove, useListener, dispatch };
};

const widgetList = [];
const widgetListeners = buildListeners();

let widgetLastId = 0;

const ExportWidget = React.memo((props) => {
  const {
    l,
    classes,

    widget
  } = props;

  React.useEffect(() => {
    widget.process();
  }, []);

  const [version, setVersion] = React.useState(0);

  widget.listeners.useListener(() => setVersion((version) => version + 1), []);

  const stateClassName =
    widget.state === 'resolve' || widget.state === 'resolve-download'
      ? classes.widgetStateSuccess
      : widget.state === 'reject'
      ? classes.widgetStateError
      : classes.widgetStateProgress;

  return (
    <Paper
      className={clsx(
        classes.widgetPapper,
        widget.isVisible && classes.widgetPapperVisible
      )}
    >
      <Typography variant="h5" component="div" color="primary">
        {widget.title}
      </Typography>

      <div className={classes.progressBlock}>
        <CircularProgress
          variant={widget.state === 'wait' ? 'indeterminate' : 'determinate'}
          className={stateClassName}
          size={120}
          value={widget.percent}
        />

        <div className={classes.progressPercent}>
          <Typography variant="h5" component="div" className={stateClassName}>
            {Math.round(widget.percent)}%
          </Typography>
        </div>
      </div>

      {widget.cancel &&
      (widget.state === 'wait' ||
        widget.state === 'progress' ||
        widget.state === 'cancel') ? (
        <Button
          color="secondary"
          disabled={widget.state === 'cancel'}
          onClick={widget.cancel}
        >
          {l('$global.buttons.cancel')}
        </Button>
      ) : widget.state === 'resolve' || widget.state === 'resolve-download' ? (
        <>
          <Typography
            variant="caption"
            component="div"
            className={stateClassName}
          >
            {l('r._.export.build.complete')}
          </Typography>

          <div className={classes.widgetControlBtns}>
            <Button
              disabled={widget.state === 'resolve-download'}
              onClick={widget.download}
            >
              {l('$global.buttons.download')}
            </Button>

            <Button color="secondary" onClick={widget.close}>
              {l('$global.buttons.close')}
            </Button>
          </div>
        </>
      ) : widget.state === 'reject' ? (
        <>
          <Typography
            variant="caption"
            component="div"
            className={stateClassName}
          >
            {widget.error}
          </Typography>

          <Button color="secondary" onClick={widget.close}>
            {l('$global.buttons.close')}
          </Button>
        </>
      ) : null}
    </Paper>
  );
});

export default withLang(
  React.memo((props) => {
    const { langInfo, getLangString: l } = props;

    const classes = useStyle();

    const [version, setVersion] = React.useState(0);

    widgetListeners.useListener(() => setVersion((version) => version + 1), []);

    return (
      <div className={classes.root}>
        {widgetList.map((widget) => (
          <widget.Component
            key={widget.id}
            langInfo={langInfo}
            l={l}
            classes={classes}
            widget={widget}
          />
        ))}
      </div>
    );
  })
);

export const addExportTask = (title, process, download, cancel) => {
  const widget = {
    Component: ExportWidget,

    listeners: buildListeners(),

    isVisible: false,
    state: 'none',
    percent: 0,
    data: null,
    error: null,

    id: ++widgetLastId,
    title,
    process: () => {
      if (widget.state !== 'none') return;

      widget.state = 'wait';

      setTimeout(() => {
        process((percent) => {
          if (widget.state === 'wait') widget.state = 'progress';

          widget.percent = Math.min(Math.max(percent, 0), 100);

          widget.listeners.dispatch();
        })
          .then((data) => {
            if (widget.state === 'cancel') {
              widget.close();
            } else if (widget.state === 'wait' || widget.state === 'progress') {
              widget.state = 'resolve';
              widget.percent = 100;
              widget.data = data;

              widget.listeners.dispatch();
            }
          })
          .catch((error) => {
            console.error(error);

            if (widget.state === 'cancel') {
              widget.close();
            } else if (widget.state === 'wait' || widget.state === 'progress') {
              widget.state = 'reject';
              widget.error = error.message;

              widget.listeners.dispatch();
            }
          });
      }, 200);
    },
    download: async () => {
      if (widget.state !== 'resolve') return;

      widget.state = 'resolve-download';

      widget.listeners.dispatch();

      try {
        await download(widget.data);

        widget.close();
      } catch (e) {
        console.error(e);

        widget.state = 'reject';
        widget.error = e.message;

        widget.listeners.dispatch();
      }
    },
    cancel: cancel
      ? () => {
          if (widget.state === 'wait' || widget.state === 'progress')
            widget.state = 'cancel';

          cancel();

          widget.listeners.dispatch();
        }
      : null,
    close: () => {
      widget.isVisible = false;

      setTimeout(() => widget.remove(), 150);

      widget.listeners.dispatch();
    },
    remove: () => {
      const index = widgetList.indexOf(widget);

      if (index >= 0) widgetList.splice(index, 1);

      widgetListeners.dispatch();
    }
  };

  widgetList.push(widget);

  widgetListeners.dispatch();

  setTimeout(() => {
    widget.isVisible = true;
    widget.listeners.dispatch();
  }, 50);
};
